/**
 * Step carousel: every `interval` ms the track slides one item to the left,
 * then the first item is moved to the end (infinite loop). Used by the
 * speakers carousel and the partner reasons carousel.
 */
export default (options = {}) => ({
  interval: options.interval ?? 5000,
  duration: options.duration ?? 700,
  canStep: false,
  timer: null,
  busy: false,
  paused: false,

  init() {
    this.measure();
    this.resizeHandler = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.measure();
        this.start();
      }, 150);
    };
    this.visibilityHandler = () => {
      document.hidden ? this.stop() : this.start();
    };
    window.addEventListener('resize', this.resizeHandler);
    document.addEventListener('visibilitychange', this.visibilityHandler);
    this.start();
  },

  destroy() {
    this.stop();
    clearTimeout(this.resizeTimer);
    window.removeEventListener('resize', this.resizeHandler);
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  },

  reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  },

  gap() {
    const style = window.getComputedStyle(this.$refs.track);
    return parseFloat(style.columnGap || style.gap) || 0;
  },

  stepSize() {
    return this.$refs.track.firstElementChild.offsetWidth + this.gap();
  },

  measure() {
    const track = this.$refs.track;
    this.canStep = track.children.length > 1 && track.scrollWidth > this.$refs.viewport.clientWidth + 1;
  },

  restartProgress() {
    const bar = this.$refs.progress;
    if (!bar) {
      return;
    }
    bar.style.animation = 'none';
    void bar.offsetWidth;
    bar.style.animation = '';
    bar.style.animationDuration = `${this.interval}ms`;
    bar.style.animationPlayState = 'running';
  },

  start() {
    this.stop();
    if (!this.canStep || this.paused || this.reducedMotion() || document.hidden) {
      return;
    }
    this.restartProgress();
    this.timer = setTimeout(() => this.next(), this.interval);
  },

  stop() {
    clearTimeout(this.timer);
    this.timer = null;
  },

  pause() {
    this.paused = true;
    this.stop();
    if (this.$refs.progress) {
      this.$refs.progress.style.animationPlayState = 'paused';
    }
  },

  resume() {
    this.paused = false;
    this.start();
  },

  next() {
    if (!this.canStep || this.busy) {
      return;
    }
    this.busy = true;
    const track = this.$refs.track;
    track.style.transition = `transform ${this.duration}ms cubic-bezier(.4, 0, .2, 1)`;
    track.style.transform = `translateX(-${this.stepSize()}px)`;
    setTimeout(() => {
      track.style.transition = 'none';
      track.appendChild(track.firstElementChild);
      track.style.transform = 'translateX(0)';
      void track.offsetWidth;
      this.busy = false;
    }, this.duration + 30);
    this.start();
  },
});
